// src/components/RedirectHandler.js

import React, { useEffect, useContext, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { AuthContext } from '../context/AuthContext';
import { jwtDecode } from 'jwt-decode'; // Correct import
import axios from 'axios';

const RedirectHandler = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const {
    setJwtToken,
    setAccessToken,
    setRefreshToken,
    setUser,
    setDiscordUserId,
    setDiscordUsername,
    setAvatarUrl,
    setIsSubscriptionFlow,
    setUserType,
    setThreadId,
  } = useContext(AuthContext);
  const isHandled = useRef(false); // Prevent multiple handling

  console.log('RedirectHandler mounted.');

  useEffect(() => {
    if (isHandled.current) return; // Exit if already handled
    isHandled.current = true; // Mark as handled

    const searchParams = new URLSearchParams(location.search);
    const code = searchParams.get('code');
    const state = searchParams.get('state'); // 'admin', 'subscribe', or 'user'

    console.log('RedirectHandler Params:', { code, state });

    if (!code) {
      console.error('Authorization code is missing.');
      // Optionally, handle error cases (e.g., display error message)
      navigate('/');
      return;
    }

    const handleRedirect = async () => {
      if (state === 'admin' || state === 'subscribe') {
        // Handle Admin or Subscription Authentication
        try {
          console.log('Sending POST request to /api/auth/discord with code:', code);

          const response = await axios.post(
            `${process.env.REACT_APP_BACKEND_URL}/api/auth/discord`,
            { code }
          );

          console.log('Response from /api/auth/discord:', response.data);

          const { jwtToken, discordUserId: fetchedDiscordUserId, accessToken, refreshToken } = response.data;

          console.log('Extracted Tokens:', { jwtToken, accessToken, fetchedDiscordUserId, refreshToken });

          // Validate response data
          if (jwtToken && accessToken && fetchedDiscordUserId) { // Corrected condition
            // Store tokens in localStorage
            localStorage.setItem('jwtToken', jwtToken);
            localStorage.setItem('discordUserId', fetchedDiscordUserId);
            localStorage.setItem('accessToken', accessToken);
            localStorage.setItem('refreshToken', refreshToken);

            console.log('Tokens stored in localStorage.');

            // Set jwtToken in Auth Context
            setJwtToken(jwtToken);
            console.log('setJwtToken called with:', jwtToken);

            // Decode JWT and set user
            try {
              const decoded = jwtDecode(jwtToken);
              console.log('Decoded JWT:', decoded);

              const userData = {
                discordUserId: decoded.discordUserId,
                username: decoded.username,
                discriminator: decoded.discriminator,
                avatar: decoded.avatar,
                email: decoded.email,
                state: decoded.state,
              };
              setUser(userData);
              console.log('User data set in context:', userData);

              // Store user data in localStorage
              localStorage.setItem('user', JSON.stringify(userData));
              console.log('User data stored in localStorage.');
            } catch (decodeError) {
              console.error('Error decoding JWT:', decodeError);
              navigate('/');
              return;
            }

            // Update Auth Context with tokens
            setAccessToken(accessToken);
            console.log('setAccessToken called with:', accessToken);
            setDiscordUserId(fetchedDiscordUserId);
            setRefreshToken(refreshToken);
            console.log('setDiscordUserId and setRefreshToken called.');

            // Set userType based on 'state'
            if (state === 'subscribe') {
              setUserType('subscribe');
              localStorage.setItem('userType', 'subscribe');
              console.log('UserType set to subscribe');
            } else {
              setUserType('admin');
              localStorage.setItem('userType', 'admin');
              console.log('UserType set to admin');
            }

            if (state === 'subscribe') {
              // Set subscription flow flag
              setIsSubscriptionFlow(true);
              console.log('isSubscriptionFlow set to true');

              // Navigate to home where the guild selection will occur
              navigate('/', { replace: true });
            } else {
              // Navigate to setup
              navigate('/setup', { replace: true });
            }
          } else {
            console.error('Missing tokens in admin authentication response.');
            navigate('/', { replace: true });
          }
        } catch (error) {
          console.error('Error during admin authentication:', error.response?.data || error.message);
          navigate('/', { replace: true });
        }

      } else if (state === 'user') {
        // Handle NFT Verification Authentication
        try {
          // Retrieve guildId and threadId from localStorage
          const guildId = localStorage.getItem('guildId');
          const threadId = localStorage.getItem('threadId');

          console.log('RedirectHandler - Retrieved guildId:', guildId);
          console.log('RedirectHandler - Retrieved threadId:', threadId);

          if (!guildId || !threadId) {
            console.error('Missing guildId or threadId for NFT verification.');
            alert('Verification context is missing. Please try again.');
            navigate('/', { replace: true });
            return;
          }

          // Log the parameters being sent
          console.log('Sending POST request to /api/auth/discord-verification with:', {
            code,
            guildId,
            threadId,
          });

          // Send code, guildId, and threadId to backend
          const response = await axios.post(
            `${process.env.REACT_APP_BACKEND_URL}/api/auth/discord-verification`,
            {
              code,
              guildId,
              threadId,
            }
          );

          console.log('Response from /api/auth/discord-verification:', response.data);

          const { jwtToken, accessToken, discordUserId, discordUsername, avatarUrl } = response.data;

          console.log('Extracted Tokens and User Info:', { jwtToken, accessToken, discordUserId, discordUsername, avatarUrl });

          // Validate response data
          if (jwtToken && accessToken && discordUserId && discordUsername) { // Corrected condition
            // Store tokens in localStorage
            localStorage.setItem('jwtToken', jwtToken); 
            localStorage.setItem('accessToken', accessToken);
            localStorage.setItem('discordUserId', discordUserId);
            localStorage.setItem('discordUsername', discordUsername);
            localStorage.setItem('avatarUrl', avatarUrl);
	    localStorage.setItem('threadId', threadId);

            console.log('Tokens and user info stored in localStorage.');

            // Update Auth Context
            setJwtToken(jwtToken);
            console.log('setJwtToken called with:', jwtToken);

            setAccessToken(accessToken);
            console.log('setAccessToken called with:', accessToken);

            setDiscordUserId(discordUserId);
            console.log('setDiscordUserId called with:', discordUserId);

            // Optionally, set additional user info
            setDiscordUsername(discordUsername);
            console.log('setDiscordUsername called with:', discordUsername);

            setAvatarUrl(avatarUrl);
            console.log('setAvatarUrl called with:', avatarUrl);

	    setThreadId(threadId);
	    console.log('setThreadId called with:', threadId);

            // Update user object
            setUser({
              discordUserId,
              username: discordUsername,
              avatar: avatarUrl,
            });
            console.log('User data set in context:', {
              discordUserId,
              username: discordUsername,
              avatar: avatarUrl,
            });

            // **Set userType to 'user'**
            setUserType('user');
            localStorage.setItem('userType', 'user');
            console.log('UserType set to user');

            navigate(`/nft-verification`, { replace: true });
          } else {
            console.error('Missing tokens in user authentication response.');
            alert('Authentication failed. Missing tokens.');
            navigate('/', { replace: true });
          }
        } catch (error) {
          console.error(
            'Error during user authentication:',
            error.response?.data || error.message
          );
          alert('Authentication failed. Please try again.');
          navigate('/', { replace: true });
        }
      } else {
        console.warn('Unknown state parameter:', state);
        navigate('/', { replace: true });
      }
    };

    const checkSubscriptionStatus = async (discordUserId) => {
      try {
        // Retrieve the guild ID (ensure it's stored somewhere accessible)
        const guildId = localStorage.getItem('selectedGuildId'); // Or retrieve from context

        if (!guildId) {
          console.error('Guild ID is missing.');
          navigate('/', { replace: true });
          return;
        }

        // Fetch server configuration
        const response = await axios.get(
          `${process.env.REACT_APP_BACKEND_URL}/api/server-config?guildId=${guildId}`
        );

        const serverConfig = response.data;
        const now = new Date();
        const subscriptionExpiresAt = new Date(serverConfig.subscriptionExpiresAt);

        if (subscriptionExpiresAt > now) {
          // Subscription is active
          navigate('/setup', { replace: true });
        } else {
          // Subscription is inactive
          // Proceed with subscription purchase flow
          navigate('/', { replace: true }); // Redirect to home or subscription page
        }
      } catch (error) {
        console.error('Error fetching server configuration:', error);
        navigate('/', { replace: true });
      }
    };

    handleRedirect();
  }, [
    location,
    navigate,
    setJwtToken,
    setAccessToken,
    setRefreshToken,
    setUser,
    setDiscordUserId,
    setDiscordUsername,
    setAvatarUrl,
    setIsSubscriptionFlow,
    setUserType,
    setThreadId,
  ]);

  return null; // This component doesn't render anything
};

export default RedirectHandler;

